import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { throttle } from "../../../../../utils/function"
import { joinLobbyRoom } from "../../../game/lobby-logic"
import { useAppDispatch, useAppSelector } from "../../../hooks"
import { logIn, logOut } from "../../../stores/NetworkStore"
import { connectWallet, connectGoogle, disconnect } from "../../../network"
import { LocalStoreKeys, localStore } from "../../utils/store"

import "./login.css"

export default function Login() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const uid = useAppSelector((state) => state.network.uid)
  const walletAddress = useAppSelector((state) => state.network.walletAddress)
  const displayName = useAppSelector((state) => state.network.displayName)
  const [prejoining, setPrejoining] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [phantomInstalled, setPhantomInstalled] = useState(false)

  useEffect(() => {
    // Check if Phantom is installed
    const checkPhantom = () => {
      const phantom = (window as any).phantom?.solana
      setPhantomInstalled(!!phantom?.isPhantom)
    }

    checkPhantom()

    // Also check after a short delay (Phantom may inject later)
    const timeout = setTimeout(checkPhantom, 500)
    return () => clearTimeout(timeout)
  }, [])

  useEffect(() => {
    // Try to restore session on mount
    const storedSession = localStore.get(LocalStoreKeys.SESSION_TOKEN)
    if (storedSession?.sessionToken && !uid) {
      restoreSession()
    }
  }, [])

  const restoreSession = async () => {
    try {
      setConnecting(true)
      const response = await fetch("/auth/refresh", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStore.get(LocalStoreKeys.SESSION_TOKEN)?.sessionToken}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        dispatch(logIn({
          uid: data.uid,
          displayName: data.displayName,
          sessionToken: data.sessionToken,
          walletAddress: data.walletAddress || null,
          authMethod: data.authMethod || "phantom"
        }))
        localStore.set(LocalStoreKeys.SESSION_TOKEN, {
          sessionToken: data.sessionToken,
          uid: data.uid,
          authMethod: data.authMethod
        }, 60 * 60 * 24)
      } else {
        localStore.delete(LocalStoreKeys.SESSION_TOKEN)
      }
    } catch (e) {
      localStore.delete(LocalStoreKeys.SESSION_TOKEN)
    } finally {
      setConnecting(false)
    }
  }

  const preJoinLobby = throttle(async function prejoin() {
    setPrejoining(true)
    setError(null)
    return joinLobbyRoom(dispatch, navigate)
      .then(() => navigate("/lobby"))
      .catch((e) => {
        setError(e.message || "Failed to join lobby")
        setPrejoining(false)
      })
  }, 1000)

  const handleConnectWallet = async () => {
    setConnecting(true)
    setError(null)

    try {
      await connectWallet()
    } catch (e: any) {
      setError(e.message || "Failed to connect wallet")
    } finally {
      setConnecting(false)
    }
  }

  const handleConnectGoogle = async () => {
    setConnecting(true)
    setError(null)

    try {
      await connectGoogle()
    } catch (e: any) {
      setError(e.message || "Failed to sign in with Google")
    } finally {
      setConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    setLoggingOut(true)
    try {
      await disconnect()
      dispatch(logOut())
    } finally {
      setLoggingOut(false)
    }
  }

  const truncateAddress = (address: string) => {
    if (!address) return ""
    return `${address.slice(0, 4)}...${address.slice(-4)}`
  }

  if (!uid) {
    return (
      <div id="play-panel">
        {error && <p className="error-message">{error}</p>}

        {/* Google sign-in hidden for now
        <ul className="actions">
          <li>
            <button
              className="bubbly blue google-sign-in-btn"
              onClick={handleConnectGoogle}
              disabled={connecting}
            >
              <img src="assets/ui/google-icon.svg" width={20} height={20} alt="" />
              {connecting ? t("connecting") : t("sign_in_google")}
            </button>
          </li>
        </ul>

        <div className="auth-divider">
          <span>{t("or")}</span>
        </div>
        */}

        {!phantomInstalled ? (
          <div className="wallet-install">
            <p>{t("phantom_required")}</p>
            <a
              href="https://phantom.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="bubbly blue"
            >
              {t("install_phantom")}
            </a>
          </div>
        ) : (
          <ul className="actions">
            <li>
              <button
                className="bubbly green wallet-connect-btn"
                onClick={handleConnectWallet}
                disabled={connecting}
              >
                {connecting ? t("connecting") : t("connect_phantom")}
              </button>
            </li>
          </ul>
        )}
      </div>
    )
  } else {
    return (
      <div id="play-panel">
        {error && <p className="error-message">{error}</p>}

        <div className="wallet-info">
          {walletAddress ? (
            <span className="wallet-address">{truncateAddress(walletAddress)}</span>
          ) : (
            <span className="user-display-name">{displayName}</span>
          )}
        </div>

        <ul className="actions">
          <li>
            <button
              className="bubbly green"
              onClick={preJoinLobby}
              disabled={prejoining}
            >
              {prejoining ? t("connecting") : t("join_lobby")}
            </button>
          </li>
          <li>
            <button
              className="bubbly red"
              disabled={prejoining || loggingOut}
              onClick={handleDisconnect}
            >
              {loggingOut ? t("disconnecting") : t("disconnect")}
            </button>
          </li>
        </ul>
      </div>
    )
  }
}
